import { useMemo, useRef } from "react";
import DefaultImg from "../../../../assets/img/default-image.svg";
import Roles from "../../../../models/roles";
import { useAppSelector as useSelector } from "../../../../store";
import { TVacancyDetail } from "../../../../store/vacancy/types";
import { capitalizeName } from "../../../../utils/stringUtils";
import ActionDropdown from "../../../UI/ActionDropdown";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Candidates from "../../../UI/Icons/Candidates";
import Copy from "../../../UI/Icons/Copy";
import Eye from "../../../UI/Icons/Eye";
import NoCandidate from "../../../UI/Icons/NoCandidate";
import Star from "../../../UI/Icons/Star";
import VerticalDots from "../../../UI/Icons/VerticalDots";
import StarRating from "../../../UI/StarRating";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
import VacancyPosition from "../../../UI/VacancyPosition";
import CentreImage from "../../../Vacancy/CentreImage";
import { formatTimeframes } from "../../../Vacancy/timeframeApiHelper";

export interface IHistoryCardProps {
  vacancy: TVacancyDetail;
  handleRating?: () => void;
  onViewDetails: () => void;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  handleDuplicate: () => void;
  handleProfileView: () => void;
  handlePreferred: () => void;
}

const HistoryCard: React.FC<IHistoryCardProps> = ({
  vacancy,
  handleRating,
  onViewDetails,
  showDropdown,
  setShowDropdown,
  handleDuplicate,
  handleProfileView,
  handlePreferred,
}) => {
  const dropdownRef = useRef<HTMLButtonElement | null>(null);
  const formattedTimeframes = formatTimeframes(vacancy.timeframes);
  const candidateInfo = {
    name: `${capitalizeName(vacancy?.selected_candidate?.first_name ?? "")} ${capitalizeName(vacancy?.selected_candidate?.last_name ?? "")}`,
    rating:
      vacancy?.selected_candidate?.rating?.latest_rating ||
      vacancy?.selected_candidate?.rating?.latest_rating ||
      0,
    isPreferred: vacancy?.selected_candidate?.is_preferred_candidate,
  };

  const user = useSelector((state) => state.auth.user);

  const isAdmin = useMemo(() => {
    const roleName = user?.role?.name;
    return roleName === Roles.SUPER_ADMIN || roleName === Roles.GENERAL_ADMIN;
  }, [user?.role?.name]);

  const dropdownItems = [
    ...(vacancy?.selected_candidate
      ? [
          {
            label: "View Candidate Profile",
            icon: <Candidates />,
            onClick: handleProfileView,
          },
          {
            label: "Mark Candidate as Preferred",
            icon: <Star />,
            onClick: handlePreferred,
          },
        ]
      : []),
    {
      label: "Duplicate as New Vacancy",
      icon: <Copy />,
      onClick: handleDuplicate,
    },
  ];

  return (
    <div className={`bg-white p-15px d-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black ${!isAdmin && vacancy?.selected_candidate?.is_documents_expired ? "disabled-lineitem" : ""}`}>
      <div className="flex-grow-1 w-100 text-break">
        <div className="d-flex gap-20 align-items-center">
          <div className="w-50px h-50px  radius-50px">
            <CentreImage vacancy={vacancy} height="50px" width="50px" />
          </div>
          <div className="flex-grow-1 d-flex gap-10 justify-content-between">
            <div className="d-flex flex-column gap-3 text-black">
              <div className="fs-16 fw-700">
                <VacancyPosition
                  positions={vacancy?.vacancy_positions}
                  alternatePosition={vacancy?.alternate_position_legacy || ""}
                />
              </div>
              <div className="fs-13 fw-400">{`${vacancy?.centre?.business_name}, ${vacancy?.centre?.address}`}</div>
            </div>
            <div className="min-w-24px position-relative d-block d-lg-none">
              <CustomButton
                variant={"primary"}
                ref={dropdownRef}
                type="button"
                className="btn-theme bg-white p-0 h-auto"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <VerticalDots color={!showDropdown ? "#aaaaaa" : "#000000"} />
              </CustomButton>
              <ActionDropdown
                options={dropdownItems}
                isOpen={showDropdown}
                onClose={() => setShowDropdown(false)}
                toggleRef={dropdownRef}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="min-w-320px max-w-320px d-flex gap-20 align-items-center text-break">
        {vacancy?.selected_candidate ? (
          <>
            <div className="w-50px h-50px  radius-50px">
              <img
                alt="Candidate"
                width="50px"
                height="50px"
                className="img-responsive rounded-circle object-fit-cover"
                src={vacancy?.selected_candidate?.photo || DefaultImg}
              />
            </div>
            <div className="d-flex flex-column gap-3 text-black">
              <div className="fs-16 fw-700">
                {`${capitalizeName(vacancy.selected_candidate.first_name ?? "")} ${capitalizeName(vacancy.selected_candidate.last_name ?? "")}`}
              </div>
              <div className="d-flex gap-10 align-items-center">
                <StarRating
                  rating={
                    isAdmin
                      ? vacancy?.selected_candidate?.rating?.average_rating || 0
                      : vacancy?.selected_candidate?.rating?.latest_rating ||
                        vacancy?.selected_candidate?.rating?.average_rating ||
                        0
                  }
                  color={
                    isAdmin ||
                    !vacancy?.selected_candidate?.rating?.latest_rating
                      ? "#aaaaaa"
                      : "#51C2B9"
                  }
                />
                {candidateInfo.isPreferred && (
                  <div className="bg-teal-pale border border-teal-dark py-4px px-8px radius-8px text-teal-dark fs-13 lh-14px">
                    Preferred
                  </div>
                )}
                {!vacancy?.selected_candidate?.rating?.latest_rating &&
                  !isAdmin && (
                    <div
                      className="fs-10 fw-400 text-alert cursor-pointer"
                      onClick={handleRating}
                    >
                      Awaiting Your Rating
                    </div>
                  )}
              </div>
              {!isAdmin && vacancy?.selected_candidate?.is_documents_expired ? (
                <ErrorMessage className="align-items-center gap-3 lh-1" message="Unavailable for work" />
              ) : ""}
            </div>
          </>
        ) : (
          <div className="d-flex gap-20 align-items-center">
            <NoCandidate />
            <div className="fs-13 fw-400 text-grey-dark">
              No Candidate Selected
            </div>
          </div>
        )}
      </div>
      <div className="min-w-370px w-370px mobile-w-100 text-break">
        {formattedTimeframes.map((ftf, i) => (
          <div key={i} className="fs-13 fw-400 text-black">
            {ftf.label}
          </div>
        ))}
      </div>
      <div className="min-w-165px mobile-w-100">
        <CustomButton
          variant={"primary"}
          type="button"
          className="btn-theme bg-off-white text-black w-100"
          onClick={onViewDetails}
        >
          <Eye color="black" />
          View Details
        </CustomButton>
      </div>
      <div className="min-w-24px position-relative d-none d-lg-block">
        <CustomButton
          variant={"primary"}
          ref={dropdownRef}
          type="button"
          className="btn-theme bg-white p-0 h-auto"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <VerticalDots color={!showDropdown ? "#aaaaaa" : "#000000"} />
        </CustomButton>
        <ActionDropdown
          options={dropdownItems}
          isOpen={showDropdown}
          onClose={() => setShowDropdown(false)}
          toggleRef={dropdownRef}
        />
      </div>
    </div>
    
  );
};

export default HistoryCard;
